/**
 * Estrategia de tolerancia.
 *
 * Tres regras, e violar qualquer uma produz bugs que so aparecem em producao:
 *
 * 1. NUNCA comparar float com `===`, e nunca usar epsilon ABSOLUTO em distancia.
 *    Todo teste de distancia e `d / charLength <= tol.distRel`. Isso torna os
 *    predicados invariantes por escala: um item desenhado a 400 unidades corrige
 *    igual a um desenhado a 4.
 *
 * 2. DOIS PERFIS. `STRICT_TOL` para degenerescencia interna do kernel (estas retas
 *    sao paralelas? este triangulo degenerou?). `GRADE_TOL` para os predicados
 *    semanticos -- deliberadamente generoso, porque a precisao do ponteiro e de
 *    1-2 px e reprovar uma construcao correta e inaceitavel. Essa generosidade so e
 *    segura PORQUE a checagem de robustez sob perturbacao faz a discriminacao real.
 *
 * 3. ANGULOS COMPARADOS COMO ANGULOS, via `atan2(cross, dot)` -- estavel perto de 0
 *    e de pi, ao contrario de `acos(dot/|a||b|)`.
 */

import type { Vec2 } from './vec.ts';
import { dist } from './vec.ts';

export interface Tol {
  /** Deteccao de degenerescencia/paralelismo. Relativo a magnitude, nunca cru. */
  readonly eps: number;
  /** Distancias, como fracao do comprimento caracteristico da configuracao. */
  readonly distRel: number;
  /** Angulos, em graus. */
  readonly angleDeg: number;
}

/** Correcao semantica: generoso de proposito. A robustez e quem discrimina. */
export const GRADE_TOL: Tol = { eps: 1e-9, distRel: 0.02, angleDeg: 0.6 };

/** Uso interno do kernel: degenerescencia, paralelismo, coincidencia. */
export const STRICT_TOL: Tol = { eps: 1e-12, distRel: 1e-6, angleDeg: 1e-4 };

/**
 * Comprimento caracteristico de uma configuracao de pontos.
 * Usado para normalizar TODO teste de distancia.
 */
export function charLengthOf(points: readonly Vec2[]): number {
  if (points.length < 2) return 1;
  let max = 0;
  for (let i = 0; i < points.length; i++) {
    for (let j = i + 1; j < points.length; j++) {
      const d = dist(points[i] as Vec2, points[j] as Vec2);
      if (d > max) max = d;
    }
  }
  return max > 1e-12 ? max : 1;
}
